import { HttpParams } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';

import { Observable, map, tap } from 'rxjs';

import { AsyncStatus } from '../models/async-status.type';
import { PaginatedResult } from '../models/paginated.response';
import { SharedLabelFormatResponse } from '../models/shared-label-format.model';
import { ApiService } from './api.service';

/** `Status` value for an active label format on `SharedLabelFormatResponse`. */
const ACTIVE_STATUS = 2;

/**
 * Root-scoped lookup for the company's active label/print formats.
 *
 * Mirrors {@link StoreContextService}'s style — always calls the real API
 * (not mock-gated), since this is cross-cutting reference data rather than a
 * feature's own read/write state.
 */
@Injectable({ providedIn: 'root' })
export class LabelFormatLookupService {
  private readonly api = inject(ApiService);

  private readonly _formats = signal<readonly SharedLabelFormatResponse[]>([]);
  private readonly _loadStatus = signal<AsyncStatus>('idle');

  readonly formats = this._formats.asReadonly();
  readonly loadStatus = this._loadStatus.asReadonly();
  readonly isLoading = computed(() => this._loadStatus() === 'loading');

  /** Loads every active label format in one shot (no server paging). Safe to call repeatedly. */
  loadFormats(): Observable<readonly SharedLabelFormatResponse[]> {
    this._loadStatus.set('loading');
    const params = new HttpParams().set('Status', ACTIVE_STATUS).set('ClientSidePagination', 'true');
    return this.api.get<PaginatedResult<SharedLabelFormatResponse>>('SharedLabelFormats', params).pipe(
      map(result => result.items ?? []),
      tap({
        next: items => {
          this._formats.set(items);
          this._loadStatus.set('success');
        },
        error: () => this._loadStatus.set('error')
      })
    );
  }
}
